import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Reusable pagination footer for list pages.
 *
 * Props:
 *   page             (1-based current page)
 *   pageSize         (current page size)
 *   total            (total record count from X-Total-Count header or array length)
 *   loading          (optional — disables controls while loading)
 *   onPage           (n => void) — switch to page n
 *   onPageSize       (n => void) — switch page size
 *   pageSizeOptions  (optional number[]) — defaults to [200, 500] for backwards compat.
 *                    For client-paginated lists, pass [25, 50, 100, 200].
 *
 * UX rules:
 * - Always shows "Showing 1–N of Total" + page-size toggle.
 * - Pagination buttons only render when total > pageSize.
 */
export function PaginationControls({ page, pageSize, total, loading, onPage, onPageSize, pageSizeOptions }) {
  const sizes = Array.isArray(pageSizeOptions) && pageSizeOptions.length ? pageSizeOptions : [200, 500];
  const totalNum = Number(total) || 0;
  const sizeNum = Number(pageSize) || sizes[0];
  const pageNum = Math.max(1, Number(page) || 1);
  const pageCount = Math.max(1, Math.ceil(totalNum / sizeNum));
  const start = totalNum === 0 ? 0 : (pageNum - 1) * sizeNum + 1;
  const end = Math.min(totalNum, pageNum * sizeNum);
  const showPager = totalNum > sizeNum;

  // Build a compact page list: 1 ... p-1 p p+1 ... last
  const buildPages = () => {
    const pages = new Set([1, pageCount, pageNum - 1, pageNum, pageNum + 1]);
    const arr = Array.from(pages).filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of arr) {
      if (prev && p - prev > 1) out.push("…");
      out.push(p);
      prev = p;
    }
    return out;
  };

  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const btnBase = "h-8 min-w-[32px] px-2 text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed";
  const btnActive = "h-8 min-w-[32px] px-2 text-xs font-bold border border-[#F97316] bg-[#F97316] text-white";

  return (
    <div
      data-testid="pagination-controls"
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-sm text-slate-700"
    >
      <div className="flex items-center gap-2">
        <span data-testid="pagination-summary">
          {totalNum === 0 ? "No records" : `Showing ${fmt(start)}–${fmt(end)} of ${fmt(totalNum)}`}
        </span>
        <div className="flex items-center gap-1 ml-3">
          <span className="text-xs text-slate-500">Page size</span>
          {sizes.map((sz) => (
            <button
              key={sz}
              type="button"
              data-testid={`page-size-${sz}`}
              onClick={() => onPageSize?.(sz)}
              disabled={loading}
              className={sizeNum === sz ? btnActive : btnBase}
            >
              {sz}
            </button>
          ))}
        </div>
      </div>

      {showPager && (
        <div className="flex items-center gap-1" data-testid="pagination-pager">
          <button type="button" data-testid="pager-first" onClick={() => onPage?.(1)} disabled={loading || pageNum <= 1} className={btnBase}>
            <ChevronsLeft className="w-3.5 h-3.5" />
          </button>
          <button type="button" data-testid="pager-prev" onClick={() => onPage?.(pageNum - 1)} disabled={loading || pageNum <= 1} className={btnBase}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          {buildPages().map((p, i) =>
            p === "…" ? (
              <span key={`ellipsis-${i}`} className="px-1 text-slate-400">…</span>
            ) : (
              <button
                key={p}
                type="button"
                data-testid={`pager-page-${p}`}
                onClick={() => onPage?.(p)}
                disabled={loading || p === pageNum}
                className={p === pageNum ? btnActive : btnBase}
              >
                {p}
              </button>
            )
          )}
          <button type="button" data-testid="pager-next" onClick={() => onPage?.(pageNum + 1)} disabled={loading || pageNum >= pageCount} className={btnBase}>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button type="button" data-testid="pager-last" onClick={() => onPage?.(pageCount)} disabled={loading || pageNum >= pageCount} className={btnBase}>
            <ChevronsRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default PaginationControls;
